/**
 * Writing a workspace's YAML file map into its folder — the one place that
 * does it, for both writers (gitFlush for sync-engine workspaces,
 * gitWorkspaceStore.saveGitSnapshot for the rest). Split out of gitFlush.js
 * on 01.10.2026 (git sync Stage 1, card 3): saveGitSnapshot rewrote every
 * file on every save, never told the watcher the writes were its own, and
 * never deleted a file — so a request deleted in Apiboo came back from disk
 * on the next load.
 *
 * Rules:
 *  - only files whose bytes changed are written (no mtime / git-status churn);
 *  - every write and delete is recorded as Apiboo's own (gitOwnWrites.js), so
 *    the file watcher does not apply it back;
 *  - orphans — managed *.yaml files the serializer no longer emits — are
 *    deleted, EXCEPT files listed in `keep` (files that did not load: deleting
 *    them would destroy what the user is in the middle of fixing);
 *  - nothing outside the managed roots is touched (README, LICENSE, code…).
 *
 * Workspace repositories manage *.yaml (the defaults below). A scope
 * repository (boards / documents / projects, git sync Stage 2 — see
 * services/git/scopeRepoLayout.js) passes its own `roots` and `isManaged`
 * (*.yaml, *.md and media files) — and a file's content may then be BYTES
 * (Uint8Array, an image), compared and written as such.
 */
import { recordOwnDelete, recordOwnWrite } from './gitOwnWrites.js';
import { mergeManagedBlock } from '../utils/yamlWorkspace/schema.js';
import { keepRepoIdentity } from './git/repoIdentity.js';

/** Directories owned by the serializer; only *.yaml inside them is managed. */
export const MANAGED_ROOTS = ['variables', 'environments', 'collections', 'schemas'];
/** Top-level files owned by the serializer. */
export const MANAGED_TOP_FILES = new Set(['apiboo.yaml', '.gitignore', '.gitattributes']);
/** Files Apiboo shares with the user: only its marked block is Apiboo's. */
const SHARED_FILES = new Set(['.gitignore', '.gitattributes']);

export async function readTextSafe(fs, absPath) {
  try {
    return await fs.readTextFile(absPath);
  } catch {
    return null;
  }
}

const isYaml = (rel) => rel.endsWith('.yaml');
const isBytes = (v) => v instanceof Uint8Array;

/** A file's size, or null when the fs cannot tell (no stat grant there, or no file). */
async function sizeSafe(fs, absPath) {
  try {
    const info = typeof fs.stat === 'function' ? await fs.stat(absPath) : null;
    return typeof info?.size === 'number' ? info.size : null;
  } catch {
    return null;
  }
}

async function readBytesSafe(fs, absPath) {
  try {
    return new Uint8Array(await fs.readFile(absPath));
  } catch {
    return null;
  }
}

function sameBytes(a, b) {
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

/** What gitOwnWrites remembers for a binary write (the watcher never reads media as text). */
function bytesMark(bytes) {
  let h = 0x811c9dc5;
  for (let i = 0; i < bytes.length; i += 1) h = Math.imul(h ^ bytes[i], 0x01000193) >>> 0;
  return `binary:${bytes.length}:${h.toString(16)}`;
}

async function ensureParent(fs, absPath) {
  const normalized = absPath.replace(/\\/g, '/');
  const dir = normalized.slice(0, normalized.lastIndexOf('/'));
  if (dir) await fs.mkdir(dir, { recursive: true });
}

export async function writeTextAbs(fs, absPath, content) {
  const normalized = absPath.replace(/\\/g, '/');
  const dir = normalized.slice(0, normalized.lastIndexOf('/'));
  if (dir) await fs.mkdir(dir, { recursive: true });
  await fs.writeTextFile(absPath, content);
}

/**
 * Recursively collect managed *.yaml files (relative paths) under `root`.
 * `roots`: the managed directories — the flat layout's by default; the nested
 * mirror layout adds its collection folders (workspaceMirror.writeMirror).
 */
export async function listManagedFiles(fs, root, roots = MANAGED_ROOTS, isManaged = isYaml) {
  const found = [];
  const walk = async (relDir) => {
    let entries = [];
    try {
      entries = await fs.readDir(`${root}/${relDir}`);
    } catch {
      return;
    }
    for (const entry of entries) {
      if (!entry?.name) continue;
      const rel = `${relDir}/${entry.name}`;
      if (entry.isDirectory) await walk(rel);
      else if (isManaged(rel)) found.push(rel);
    }
  };
  for (const managedRoot of roots) {
    await walk(managedRoot);
  }
  return found;
}

/**
 * Every *.yaml under `root`, any depth, except inside `.git` — what a commit can
 * stage. The secret scan reads these, so a layout's collection folders (the
 * nested mirror keeps them at the top level) are never skipped.
 */
export async function listAllYaml(fs, root) {
  return listAllFiles(fs, root, (rel) => rel.endsWith('.yaml'));
}

/**
 * Every file under `root` (any depth, never inside `.git`) for which
 * `include(relPath)` is true — a scope repository's secret scan reads its
 * *.yaml and *.md this way (scopeRepoLayout.isScopeDataPath).
 */
export async function listAllFiles(fs, root, include) {
  const found = [];
  const walk = async (relDir) => {
    let entries = [];
    try {
      entries = await fs.readDir(relDir ? `${root}/${relDir}` : root);
    } catch {
      return;
    }
    for (const entry of entries) {
      if (!entry?.name || entry.name === '.git') continue;
      // A link is never followed or read: a repository's content is data from
      // others, and a link could point anywhere on this machine (git sync review).
      if (entry.isSymlink) continue;
      const rel = relDir ? `${relDir}/${entry.name}` : entry.name;
      if (entry.isDirectory) await walk(rel);
      else if (include(rel)) found.push(rel);
    }
  };
  await walk('');
  return found;
}

/** Best-effort removal of directories left empty by orphan deletion. */
export async function pruneEmptyDirs(fs, root, relDir) {
  let entries = [];
  try {
    entries = await fs.readDir(`${root}/${relDir}`);
  } catch {
    return;
  }
  for (const entry of entries) {
    if (entry?.isDirectory) await pruneEmptyDirs(fs, root, `${relDir}/${entry.name}`);
  }
  try {
    const after = await fs.readDir(`${root}/${relDir}`);
    if (after.length === 0) await fs.remove(`${root}/${relDir}`);
  } catch {
    /* best-effort */
  }
}

/**
 * Bring `dir` in line with `files` (relative path → content).
 * @param {{ rewrite?: boolean, keep?: string[] }} options
 *   rewrite: the caller already cleared the folder — write everything, delete nothing.
 *   keep: relative paths never deleted as orphans.
 * @returns {Promise<{ written: number, deleted: number }>}
 */
/**
 * A file a caller wants left exactly as it is in the folder (git sync Stage 2:
 * a media file from a teammate too big to read) — not written, not deleted.
 */
export const KEEP_FILE = Symbol('keep-file');

export async function writeWorkspaceFiles(
  fs,
  dir,
  files,
  { rewrite = false, keep = [], roots = MANAGED_ROOTS, isManaged = isYaml, topFiles = MANAGED_TOP_FILES } = {}
) {
  let written = 0;
  for (const [relPath, generated] of Object.entries(files)) {
    const abs = `${dir}/${relPath}`;
    if (generated === KEEP_FILE) continue; // the repository's own copy stays — neither rewritten nor deleted
    if (isBytes(generated)) {
      // Size first (when the fs can tell it): a file of another size differs — no need to load it to compare.
      const size = rewrite ? null : await sizeSafe(fs, abs);
      const same = !rewrite && (size == null || size === generated.length) && sameBytes(await readBytesSafe(fs, abs), generated);
      if (same) continue;
      recordOwnWrite(abs, bytesMark(generated));
      await ensureParent(fs, abs);
      await fs.writeFile(abs, generated);
      written += 1;
      continue;
    }
    const existing = rewrite ? null : await readTextSafe(fs, abs);
    const content = SHARED_FILES.has(relPath)
      ? mergeManagedBlock(existing, generated)
      : relPath === 'apiboo.yaml'
        ? keepRepoIdentity(existing, generated)
        : generated;
    if (existing === content) continue;
    // Remembered BEFORE the write: the watcher event can arrive before the
    // write resolves, and it must already know this is our own.
    recordOwnWrite(abs, content);
    await writeTextAbs(fs, abs, content);
    written += 1;
  }

  let deleted = 0;
  if (!rewrite) {
    const kept = new Set(keep);
    const onDisk = await listManagedFiles(fs, dir, roots, isManaged);
    for (const rel of onDisk) {
      if (files[rel] !== undefined || topFiles.has(rel) || kept.has(rel)) continue;
      try {
        recordOwnDelete(`${dir}/${rel}`);
        await fs.remove(`${dir}/${rel}`);
        deleted += 1;
      } catch {
        /* best-effort */
      }
    }
    if (deleted > 0) {
      for (const managedRoot of roots) {
        await pruneEmptyDirs(fs, dir, managedRoot);
      }
    }
  }
  return { written, deleted };
}
